import { Alert, Badge, Card, Divider, Group, SimpleGrid, Skeleton, Stack, Table, Text, Timeline, Title } from "@mantine/core";
import { IconHistory, IconInfoCircle, IconPointFilled, IconReceiptOff, IconTags } from "@tabler/icons-react";
import { useState } from "react";
import useSWR from "swr";
import { TenantFeePolicyAdminApi } from "../../../../apis";
import type { FeePolicy, FeePolicyVersion, CalculationMethod } from "../../../../apis/tenantFeePolicy";
import { EmptyState } from "../../../../components/billing/EmptyState";
import { FeePolicyStatusBadge } from "../../../../components/billing/FeePolicyStatusBadge";
import { PolicyReadOnlyDrawer } from "../../../../components/billing/PolicyReadOnlyDrawer";
import { SectionHeader } from "../../../../components/billing/SectionHeader";
import { formatDate, formatMoney, formatPercent } from "../../../../utils/format";

// ── Helpers ──────────────────────────────────────────────────────────────────

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

const METHOD_LABEL: Record<string, string> = { PERCENTAGE: "Хувь", FLAT: "Тогтмол", TIERED: "Шатлалт" };

const FEE_BASIS_LABEL: Record<string, string> = {
  ORDER_TOTAL    : "Захиалгын дүн",
  PAYMENT        : "Төлбөрийн дүн",
  NET            : "Цэвэр дүн",
  SUBTOTAL_EX_TAX: "Татвар хассан",
};

const DEDUCTION_MODE_LABEL: Record<string, string> = {
  AUTO_DEDUCT     : "Auto deduct",
  SEPARATE_INVOICE: "Тусдаа нэхэмжлэх",
  OFFSET_PAYOUT   : "Payout offset",
  EXTERNAL        : "Гадуур",
};

const REFUND_POLICY_LABEL: Record<string, string> = {
  PRO_RATA : "Pro-rata",
  NONE     : "Буцаахгүй",
  FULL_ONLY: "Зөвхөн бүрэн буцаалт",
};

function methodHeadline(p: FeePolicy): string {
  if (p.calculationMethod === "PERCENTAGE" && p.feeRate) return formatPercent(Number(p.feeRate) * 100);
  if (p.calculationMethod === "FLAT" && p.flatFeeAmount) return formatMoney(p.flatFeeAmount, p.feeCurrency);
  return METHOD_LABEL[p.calculationMethod] ?? p.calculationMethod;
}

function methodChip(method: CalculationMethod): React.ReactNode {
  return <Badge color="gray" variant="light" radius="sm">{METHOD_LABEL[method] ?? method}</Badge>;
}

function methodRate(p: FeePolicy): string {
  if (p.calculationMethod === "PERCENTAGE") return p.feeRate ? formatPercent(Number(p.feeRate) * 100) : "—";
  if (p.calculationMethod === "FLAT") return formatMoney(p.flatFeeAmount, p.feeCurrency);
  return "";
}

// ── Compact field ─────────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>{label}</Text>
      <Text size="sm" fw={500} style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
    </Stack>
  );
}

// ── DEFAULT policy hero card (read-only) ────────────────────────────────────────

function DefaultPolicyCard({ policy, onSelect }: { policy: FeePolicy; onSelect: () => void }) {
  const minMax =
    policy.minFeeAmount || policy.maxFeeAmount
      ? `${formatMoney(policy.minFeeAmount, policy.feeCurrency)} — ${formatMoney(policy.maxFeeAmount, policy.feeCurrency)}`
      : "—";

  const metaParts = [
    policy.currentVersion != null ? `v${policy.currentVersion}` : null,
    policy.effectiveFrom ? `${formatDate(policy.effectiveFrom)}-ээс хүчинтэй` : null,
    policy.defaultUsedByCount != null ? `${policy.defaultUsedByCount} харилцагч хэрэглэдэг` : null,
  ].filter(Boolean);

  return (
    <Card withBorder padding="lg" radius="md" onClick={onSelect} style={{ cursor: "pointer", ...heroWash }}>
      <Group justify="space-between" align="flex-start" mb="md">
        <Stack gap={6}>
          <Group gap="xs">
            <Badge color="brand" size="md" radius="sm">DEFAULT</Badge>
            <FeePolicyStatusBadge status={policy.status} />
          </Group>
          <Group gap="xs" align="baseline">
            <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
              {METHOD_LABEL[policy.calculationMethod] ?? policy.calculationMethod}
            </Text>
            <Title order={1} c="brand.7" style={{ fontVariantNumeric: "tabular-nums" }}>{methodHeadline(policy)}</Title>
          </Group>
          {metaParts.length > 0 && <Text size="xs" c="dimmed">{metaParts.join(" · ")}</Text>}
        </Stack>
      </Group>
      <Divider mb="md" />
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
        <Field label="Min / Max шимтгэл" value={minMax} />
        <Field label="Суурь" value={FEE_BASIS_LABEL[policy.feeBasis] ?? policy.feeBasis} />
        <Field label="Цуглуулалт" value={DEDUCTION_MODE_LABEL[policy.deductionMode] ?? policy.deductionMode} />
        <Field label="Буцаалт" value={REFUND_POLICY_LABEL[policy.refundPolicy] ?? policy.refundPolicy} />
        <Field label="Платформ шимтгэл үүрэх" value={policy.absorbPlatformFee ? "Тийм" : "Үгүй"} />
      </SimpleGrid>
    </Card>
  );
}

// ── NAMED policies table ──────────────────────────────────────────────────────

function NamedPoliciesTable({ policies, onSelect }: { policies: FeePolicy[]; onSelect: (p: FeePolicy) => void }) {
  if (policies.length === 0) {
    return <EmptyState icon={<IconTags size={26} />} message="Нэртэй бодлого байхгүй." />;
  }

  return (
    <Card withBorder radius="md" padding={0}>
      <Table.ScrollContainer minWidth={520}>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Нэр</Table.Th>
              <Table.Th>Арга</Table.Th>
              <Table.Th>Төлөв</Table.Th>
              <Table.Th ta="right">Харилцагч</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {policies.map((p) => (
              <Table.Tr key={p.id} onClick={() => onSelect(p)} style={{ cursor: "pointer" }}>
                <Table.Td>
                  <Text size="sm" fw={500}>{p.name ?? "—"}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    {methodChip(p.calculationMethod)}
                    <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{methodRate(p)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <FeePolicyStatusBadge status={p.status} />
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{p.assignedCustomerCount ?? 0}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

// ── Version history (DEFAULT) ─────────────────────────────────────────────────

function versionRateLabel(v: FeePolicyVersion): string {
  if (v.calculationMethod === "FLAT") return formatMoney(v.flatFeeAmount);
  if (v.feeRate != null && v.feeRate !== "") return formatPercent(Number(v.feeRate) * 100);
  return "—";
}

function DefaultVersionHistory({ tenantId, policyId }: { tenantId: string; policyId: string }) {
  const { data, isLoading, error } = useSWR(
    `swr.billing.tenant.feepolicy.versions.${tenantId}.${policyId}`,
    () => TenantFeePolicyAdminApi.getByTenant(tenantId, policyId)
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const versions: FeePolicyVersion[] = [...(((data as any)?.versions ?? []) as FeePolicyVersion[])]
    .sort((a, b) => b.version - a.version);

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <SectionHeader eyebrow="Түүх" title="Хувилбарын түүх (DEFAULT)" />
        <Divider />
        {isLoading ? (
          <Skeleton height={120} radius="sm" />
        ) : error ? (
          <Text size="sm" c="red">Түүх ачаалахад алдаа гарлаа.</Text>
        ) : versions.length === 0 ? (
          <EmptyState icon={<IconHistory size={26} />} message="Хувилбарын түүх алга." />
        ) : (
          <Timeline active={versions.length} bulletSize={22} lineWidth={2}>
            {versions.map((v) => (
              <Timeline.Item
                key={v.id}
                bullet={<IconPointFilled size={12} />}
                title={
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>v{v.version}</Text>
                    <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{versionRateLabel(v)}</Text>
                    <FeePolicyStatusBadge status={v.effectiveUntil ? "ARCHIVED" : "ACTIVE"} />
                  </Group>
                }
              >
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatDate(v.effectiveFrom)} → {v.effectiveUntil ? formatDate(v.effectiveUntil) : "одоог хүртэл"}
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Stack>
    </Card>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export function TenantFeePoliciesTab({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = useSWR(
    `swr.billing.tenant.feepolicies.${tenantId}`,
    () => TenantFeePolicyAdminApi.listByTenant(tenantId)
  );

  const [selected, setSelected] = useState<FeePolicy | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openPolicy = (p: FeePolicy) => { setSelected(p); setDrawerOpen(true); };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: FeePolicy[] = (data as any)?.rows ?? [];
  const defaultPolicy = rows.find((p) => p.scope === "DEFAULT") ?? null;
  const named         = rows.filter((p) => p.scope === "NAMED");

  return (
    <Stack gap="md">
      <PolicyReadOnlyDrawer
        policy={selected}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        hideEdit
      />
      {/* Read-only info note */}
      <Alert icon={<IconInfoCircle size={18} />} color="brand" variant="light">
        Зөвхөн харах. Тенант өөрөө удирддаг — платформ зөвхөн харна.
      </Alert>

      {isLoading ? (
        <Skeleton height={200} radius="md" />
      ) : rows.length === 0 ? (
        <Card withBorder radius="md" padding="md">
          <EmptyState icon={<IconReceiptOff size={26} />} message="Энэ тенант шимтгэлийн бодлого тохируулаагүй байна." />
        </Card>
      ) : (
        <>
          <Stack gap="sm">
            <SectionHeader eyebrow="Шимтгэл" title="Үндсэн бодлого" />
            {defaultPolicy ? (
              <DefaultPolicyCard policy={defaultPolicy} onSelect={() => openPolicy(defaultPolicy)} />
            ) : (
              <Card withBorder radius="md" padding="md">
                <Text size="sm" c="dimmed">Үндсэн бодлого тохируулагдаагүй байна.</Text>
              </Card>
            )}
          </Stack>
          <Stack gap="sm">
            <SectionHeader eyebrow="Шимтгэл" title="Нэртэй бодлогууд" />
            <NamedPoliciesTable policies={named} onSelect={openPolicy} />
          </Stack>
          {defaultPolicy && (
            <DefaultVersionHistory tenantId={tenantId} policyId={defaultPolicy.id} />
          )}
        </>
      )}
    </Stack>
  );
}
